/**
 * Positioned Phrase Plugin for ProseMirror
 *
 * This plugin tracks highlight positions as the document changes. It implements
 * three edit behaviors that determine how highlights respond to edits:
 *
 * - atomic: Highlight is removed if any character within it is modified
 * - flexible: Highlight expands/contracts with edits, removed if fully deleted
 * - sticky: Highlight stays attached to the last word, removed if fully deleted
 *
 * Based on: ai_plans/standalone-lift/src/editor/PositionedPhrasePlugin.ts
 *
 * Key differences from standalone-lift:
 * - Uses Markeroo's ShareTag type instead of Textio's Phrase type
 * - Simpler API focused on highlight popping for revision mode
 * - No dependency on external types or utilities
 */

import type { Transaction, EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

/**
 * Edit behavior determines how a highlight responds to document edits.
 */
export type EditBehavior = "atomic" | "flexible" | "sticky";

/**
 * A positioned phrase represents a highlight and its current position
 * within the ProseMirror document.
 */
export interface PositionedPhrase {
  /** Unique identifier for this phrase/highlight */
  id: string;
  /** ProseMirror position for the start of the phrase */
  startPos: number;
  /** ProseMirror position for the end of the phrase */
  endPos: number;
  /** The original phrase data (e.g., tag_id, explanation) */
  data: unknown;
  /** Edit behavior for this phrase */
  editBehavior: EditBehavior;
}

interface PluginState {
  positionedPhrases: PositionedPhrase[];
}

const initialState: PluginState = {
  positionedPhrases: [],
};

export const positionedPhrasePluginKey = new PluginKey<PluginState>(
  "positionedPhrases"
);

/**
 * Create a transaction to set new positioned phrases.
 */
export function setPositionedPhrases(
  state: EditorState,
  phrases: PositionedPhrase[]
): Transaction {
  return state.tr.setMeta(positionedPhrasePluginKey, phrases);
}

/**
 * Get the current positioned phrases from editor state.
 */
export function getPositionedPhrases(state: EditorState): PositionedPhrase[] {
  return positionedPhrasePluginKey.getState(state)?.positionedPhrases ?? [];
}

/**
 * Get positioned phrases within a given range.
 */
export function getPositionedPhrasesInRange(
  state: EditorState,
  from: number,
  to: number
): PositionedPhrase[] {
  return getPositionedPhrases(state).filter(
    (p) => p.startPos >= from && p.endPos <= to
  );
}

export interface PositionedPhrasePluginOptions {
  /**
   * Called when a phrase is removed (popped) due to document edits.
   * This is used for atomic behavior where any edit invalidates the highlight.
   */
  onPhraseRemoved?: (phrase: PositionedPhrase) => void;

  /**
   * Called when a phrase is auto-resolved due to all its text being deleted.
   * This is specifically for elastic/flexible behavior where the highlight
   * collapses to nothing. The phrase data should persist for sidebar display.
   *
   * If not provided, falls back to onPhraseRemoved.
   */
  onHighlightResolved?: (phrase: PositionedPhrase) => void;

  /**
   * Default edit behavior for phrases that don't specify one.
   * Defaults to "atomic".
   *
   * - atomic: Highlight removed if any character is modified
   * - flexible: Highlight expands/contracts with edits (elastic/Google Docs-style)
   * - sticky: Highlight stays attached to the last word
   */
  defaultEditBehavior?: EditBehavior;
}

/**
 * Creates the positioned phrase plugin.
 *
 * This plugin tracks highlight positions as the document changes and
 * calls onPhraseRemoved when a highlight is "popped" (invalidated by edits).
 * For elastic/flexible behavior, calls onHighlightResolved when all text
 * within a highlight is deleted.
 */
export function createPositionedPhrasePlugin(
  options: PositionedPhrasePluginOptions = {}
): Plugin<PluginState> {
  const {
    onPhraseRemoved,
    onHighlightResolved,
    defaultEditBehavior = "atomic",
  } = options;

  return new Plugin<PluginState>({
    key: positionedPhrasePluginKey,

    state: {
      init: () => initialState,

      apply(tr, pluginState): PluginState {
        // Check if we're setting new phrases via meta
        const newPhrases = tr.getMeta(positionedPhrasePluginKey) as
          | PositionedPhrase[]
          | undefined;

        if (newPhrases !== undefined) {
          // Track which phrases were removed
          if (onPhraseRemoved) {
            const newIds = new Set(newPhrases.map((p) => p.id));
            pluginState.positionedPhrases.forEach((oldPhrase) => {
              if (!newIds.has(oldPhrase.id)) {
                onPhraseRemoved(oldPhrase);
              }
            });
          }

          return { positionedPhrases: newPhrases };
        }

        // If document didn't change, keep current state
        if (!tr.docChanged) {
          return pluginState;
        }

        // Map phrase positions through the transaction
        const mappedPhrases = mapPositionedPhrases(
          pluginState.positionedPhrases,
          tr,
          defaultEditBehavior,
          onPhraseRemoved,
          onHighlightResolved
        );

        return { positionedPhrases: mappedPhrases };
      },
    },
  });
}

/**
 * Result of updating a phrase through a transaction.
 */
interface PhraseUpdateResult {
  /** The updated phrase, or null if it should be removed */
  phrase: PositionedPhrase | null;
  /** Whether this was an auto-resolve (all text deleted) vs. removal (edited) */
  wasAutoResolved: boolean;
}

/**
 * Map phrase positions through a transaction that changed the document.
 */
function mapPositionedPhrases(
  phrases: PositionedPhrase[],
  tr: Transaction,
  defaultEditBehavior: EditBehavior,
  onPhraseRemoved?: (phrase: PositionedPhrase) => void,
  onHighlightResolved?: (phrase: PositionedPhrase) => void
): PositionedPhrase[] {
  const oldDoc = tr.before;
  const newDoc = tr.doc;

  // Find minimum changed offset for optimization
  const minOldOffset = findMinOldOffset(tr);

  const nextPhrases: PositionedPhrase[] = [];

  phrases.forEach((phrase) => {
    // Optimization: phrases ending before any change can be kept as-is
    if (phrase.endPos < minOldOffset) {
      nextPhrases.push(phrase);
      return;
    }

    const behavior = phrase.editBehavior || defaultEditBehavior;
    let result: PhraseUpdateResult;

    switch (behavior) {
      case "atomic":
        // Atomic behavior: any edit = removal (not auto-resolve)
        result = { phrase: updateAtomicPhrase(phrase, tr, oldDoc, newDoc), wasAutoResolved: false };
        break;
      case "flexible":
        result = updateFlexiblePhraseWithResolution(phrase, tr, oldDoc);
        break;
      case "sticky":
        result = updateStickyPhraseWithResolution(phrase, tr, oldDoc, newDoc);
        break;
    }

    if (result.phrase) {
      nextPhrases.push(result.phrase);
    } else {
      // Phrase was removed or auto-resolved
      if (result.wasAutoResolved) {
        // For flexible/sticky: all text deleted = auto-resolve
        // Call onHighlightResolved if provided, otherwise fall back to onPhraseRemoved
        (onHighlightResolved ?? onPhraseRemoved)?.(phrase);
      } else {
        // For atomic: any edit = removal
        onPhraseRemoved?.(phrase);
      }
    }
  });

  return nextPhrases;
}

/**
 * Atomic behavior: phrase is removed if modified in any way.
 */
function updateAtomicPhrase(
  phrase: PositionedPhrase,
  tr: Transaction,
  oldDoc: ProseMirrorNode,
  newDoc: ProseMirrorNode
): PositionedPhrase | null {
  // Validate old position bounds
  if (phrase.startPos < 0 || phrase.endPos > oldDoc.content.size || phrase.startPos >= phrase.endPos) {
    return null;
  }

  // Map positions (1 = prefer moving right, -1 = prefer moving left)
  const newStartPos = tr.mapping.map(phrase.startPos, 1);
  const newEndPos = tr.mapping.map(phrase.endPos, -1);

  // If bounds collapsed, phrase was deleted
  if (newStartPos >= newEndPos) {
    return null;
  }

  // Validate new position bounds
  if (newStartPos < 0 || newEndPos > newDoc.content.size) {
    return null;
  }

  // Check if length changed
  const oldLength = phrase.endPos - phrase.startPos;
  const newLength = newEndPos - newStartPos;
  if (oldLength !== newLength) {
    return null;
  }

  // Check if text content changed
  try {
    const oldText = oldDoc.textBetween(phrase.startPos, phrase.endPos);
    const newText = newDoc.textBetween(newStartPos, newEndPos);
    if (oldText !== newText) {
      return null;
    }

    // Check if phrase now touches adjacent words
    // e.g., "hello world" -> "helloworld" should pop "world"
    // Use Unicode-aware regex to handle non-ASCII characters (e.g., "café", "日本語")
    const startsWithWord = /^\p{L}/u.test(oldText);
    const endsWithWord = /\p{L}$/u.test(oldText);

    if (startsWithWord && newStartPos > 0) {
      const charBefore = newDoc.textBetween(newStartPos - 1, newStartPos);
      if (/\p{L}/u.test(charBefore)) {
        return null;
      }
    }

    if (endsWithWord && newEndPos + 1 <= newDoc.content.size) {
      const charAfter = newDoc.textBetween(newEndPos, newEndPos + 1);
      if (/\p{L}/u.test(charAfter)) {
        return null;
      }
    }
  } catch (error) {
    // RangeError is expected when positions are out of bounds after edits
    // Other errors may indicate bugs and should be logged more prominently
    if (error instanceof RangeError) {
      if (process.env.NODE_ENV === "development") {
        console.debug("Atomic phrase position out of bounds:", phrase.id);
      }
    } else {
      console.warn("Unexpected error in updateAtomicPhrase:", phrase.id, error);
    }
    return null;
  }

  return { ...phrase, startPos: newStartPos, endPos: newEndPos };
}

/**
 * Flexible behavior with auto-resolve tracking.
 * Phrase expands/contracts with edits (Google Docs-style elastic behavior).
 * Returns wasAutoResolved=true when all highlighted text is deleted.
 */
function updateFlexiblePhraseWithResolution(
  phrase: PositionedPhrase,
  tr: Transaction,
  oldDoc: ProseMirrorNode
): PhraseUpdateResult {
  try {
    // Validate old position bounds before accessing text
    if (phrase.startPos < 0 || phrase.endPos > oldDoc.content.size || phrase.startPos >= phrase.endPos) {
      return { phrase: null, wasAutoResolved: true };
    }

    const oldText = oldDoc.textBetween(phrase.startPos, phrase.endPos);
    const shouldExtend = !endsWithSentenceMarker(oldText);

    const newStartPos = tr.mapping.map(phrase.startPos, -1);
    const newEndPos = tr.mapping.map(phrase.endPos, shouldExtend ? 0 : -1);

    // If bounds collapsed, phrase was deleted = auto-resolve
    if (newStartPos >= newEndPos) {
      return { phrase: null, wasAutoResolved: true };
    }

    // Validate new position bounds
    if (newStartPos < 0 || newEndPos > tr.doc.content.size) {
      return { phrase: null, wasAutoResolved: true };
    }

    return {
      phrase: { ...phrase, startPos: newStartPos, endPos: newEndPos },
      wasAutoResolved: false,
    };
  } catch (error) {
    // RangeError is expected when positions are out of bounds after edits
    // Other errors may indicate bugs and should be logged more prominently
    if (error instanceof RangeError) {
      if (process.env.NODE_ENV === "development") {
        console.debug("Flexible phrase position out of bounds (auto-resolved):", phrase.id);
      }
    } else {
      console.warn("Unexpected error in updateFlexiblePhraseWithResolution:", phrase.id, error);
    }
    return { phrase: null, wasAutoResolved: true };
  }
}

/**
 * Sticky behavior with auto-resolve tracking.
 * Phrase sticks to the last word and expands with edits.
 * Returns wasAutoResolved=true when all highlighted text is deleted.
 */
function updateStickyPhraseWithResolution(
  phrase: PositionedPhrase,
  tr: Transaction,
  oldDoc: ProseMirrorNode,
  newDoc: ProseMirrorNode
): PhraseUpdateResult {
  try {
    // Validate old position bounds before accessing text
    if (phrase.startPos < 0 || phrase.endPos > oldDoc.content.size || phrase.startPos >= phrase.endPos) {
      return { phrase: null, wasAutoResolved: true };
    }

    const oldText = oldDoc.textBetween(phrase.startPos, phrase.endPos);
    const shouldExtend = !endsWithSentenceMarker(oldText);

    const newStartResult = tr.mapping.mapResult(phrase.startPos, -1);
    const newStartPos = newStartResult.pos;
    const newEndPos = tr.mapping.map(phrase.endPos, shouldExtend ? 0 : -1);

    // If bounds collapsed, phrase was deleted = auto-resolve
    if (newStartPos >= newEndPos) {
      return { phrase: null, wasAutoResolved: true };
    }

    // Validate new position bounds
    if (newStartPos < 0 || newEndPos > newDoc.content.size) {
      return { phrase: null, wasAutoResolved: true };
    }

    // Check if text changed
    const oldLength = phrase.endPos - phrase.startPos;
    const newLength = newEndPos - newStartPos;

    const newText = newDoc.textBetween(newStartPos, newEndPos);

    if (oldLength !== newLength || oldText !== newText || newStartResult.deletedBefore) {
      // Find the last space to stick to the last word
      const $start = newDoc.resolve(newStartPos);
      const parentStart = $start.start();

      // Validate parent position bounds
      if (parentStart < 0 || parentStart > newEndPos) {
        return { phrase: null, wasAutoResolved: true };
      }

      const blockText = newDoc.textBetween(parentStart, newEndPos);

      const lastSpaceOffset = blockText.lastIndexOf(" ");
      if (lastSpaceOffset >= 0) {
        // Calculate new start position after the last space
        const adjustedStartPos = parentStart + lastSpaceOffset + 1;
        if (adjustedStartPos < newEndPos) {
          return {
            phrase: { ...phrase, startPos: adjustedStartPos, endPos: newEndPos },
            wasAutoResolved: false,
          };
        }
      }
    }

    return {
      phrase: { ...phrase, startPos: newStartPos, endPos: newEndPos },
      wasAutoResolved: false,
    };
  } catch (error) {
    // RangeError is expected when positions are out of bounds after edits
    // Other errors may indicate bugs and should be logged more prominently
    if (error instanceof RangeError) {
      if (process.env.NODE_ENV === "development") {
        console.debug("Sticky phrase position out of bounds (auto-resolved):", phrase.id);
      }
    } else {
      console.warn("Unexpected error in updateStickyPhraseWithResolution:", phrase.id, error);
    }
    return { phrase: null, wasAutoResolved: true };
  }
}

/**
 * Check if text ends with a sentence marker.
 */
function endsWithSentenceMarker(text: string): boolean {
  return /[.!?\n]$/.test(text);
}

/**
 * Find the minimum offset that was changed in the transaction.
 * Used for optimization - phrases ending before this can be skipped.
 */
function findMinOldOffset(tr: Transaction): number {
  let minOffset = tr.before.nodeSize;

  tr.mapping.maps.forEach((map) => {
    map.forEach((oldStart) => {
      if (oldStart < minOffset) {
        minOffset = oldStart;
      }
    });
  });

  // Subtract 1 to ensure adjacent phrases are processed
  return minOffset - 1;
}

/**
 * Categorize changes between old and new phrase sets.
 * Useful for tracking which phrases were added/removed/kept.
 */
export function categorizePhraseChanges(
  oldPhrases: PositionedPhrase[],
  newPhrases: PositionedPhrase[]
): {
  added: PositionedPhrase[];
  removed: PositionedPhrase[];
  kept: PositionedPhrase[];
} {
  const oldById = new Map(oldPhrases.map((p) => [p.id, p]));
  const newById = new Map(newPhrases.map((p) => [p.id, p]));

  const added: PositionedPhrase[] = [];
  const kept: PositionedPhrase[] = [];
  const removed: PositionedPhrase[] = [];

  // Find added and kept
  newPhrases.forEach((phrase) => {
    if (oldById.has(phrase.id)) {
      kept.push(phrase);
    } else {
      added.push(phrase);
    }
  });

  // Find removed
  oldPhrases.forEach((phrase) => {
    if (!newById.has(phrase.id)) {
      removed.push(phrase);
    }
  });

  return { added, removed, kept };
}
